import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { isMilestone } from "@/components/StreakShare";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Member progress for the dashboard: check-in streak, module completion, reminders, welcome + tour flags.
export const useDashboardProgress = ({ user, onShareModule, onShareStreak }) => {
  const [progress, setProgress] = useState(null);
  const [videosAvailable, setVideosAvailable] = useState([]);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/modules/videos/index`).then(({ data }) => setVideosAvailable(data.available || [])).catch(() => {});
    if (user) axios.get(`${API}/progress/me`).then(({ data }) => setProgress(data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!progress || progress.welcome_seen) return undefined;
    let alive = true;
    fetch("/videos/welcome.jpg", { method: "HEAD" })
      .then((r) => {
        const exists = r.ok && (r.headers.get("content-type") || "").startsWith("image/");
        if (!alive) return;
        if (exists) setWelcomeOpen(true);
        else setProgress((p) => ({ ...(p || {}), welcome_seen: true }));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [progress]);

  useEffect(() => {
    if (!progress || progress.tour_done || !progress.welcome_seen) return undefined;
    const t = setTimeout(() => setTourOpen(true), 900);
    return () => clearTimeout(t);
  }, [progress]);

  const closeWelcome = useCallback(() => {
    setWelcomeOpen(false);
    setProgress((p) => ({ ...(p || {}), welcome_seen: true }));
    axios.post(`${API}/progress/welcome`).catch(() => {});
  }, []);

  const finishTour = useCallback(() => {
    setTourOpen(false);
    setProgress((p) => ({ ...(p || {}), tour_done: true }));
    axios.post(`${API}/progress/tour`).catch(() => {});
  }, []);

  const applyCheckin = (data) => {
    setProgress((p) => ({
      ...(p || {}), streak_current: data.streak_current, streak_longest: data.streak_longest, checked_in_today: true,
      grace_pending: false, grace_available: data.grace_used ? false : (p?.grace_available ?? true),
    }));
    if (data.status === "checked_in" && data.grace_used) {
      toast.success(`Grace day used — your streak survived. Day ${data.streak_current}.`, { description: "One grace day a month. Back to daily from here.", duration: 8000 });
    }
  };

  const checkin = async () => {
    try {
      const { data } = await axios.post(`${API}/progress/checkin`);
      applyCheckin(data);
      if (data.status !== "checked_in" || data.grace_used) return;
      if (isMilestone(data.streak_current)) {
        toast.success(`${data.streak_current} days. That is a milestone.`, { action: { label: "Share it", onClick: onShareStreak }, duration: 8000 });
      } else {
        toast.success(`Day ${data.streak_current}. Streak alive.`);
      }
    } catch {
      toast.error("Could not check in. Try again.");
    }
  };

  const toggleDone = async (mid) => {
    const wasDone = progress?.completed_modules?.includes(mid);
    try {
      const { data } = await axios.post(`${API}/progress/modules/${mid}`, { done: !wasDone });
      setProgress((p) => ({ ...(p || {}), ...data }));
      if (data.graduated && !wasDone) toast.success("All 8 complete — your graduation is waiting below.");
      else if (!wasDone) toast.success(`Module ${mid.replace("m", "0")} done.`, { action: { label: "Share it", onClick: () => onShareModule(mid) } });
    } catch {
      toast.error("Could not save. Try again.");
    }
  };

  const toggleReminder = async () => {
    try {
      const { data } = await axios.post(`${API}/progress/reminder`, { enabled: !progress?.daily_reminder });
      setProgress((p) => ({ ...(p || {}), daily_reminder: data.daily_reminder }));
      toast.success(data.daily_reminder ? "Daily reminder on — we'll nudge you each morning." : "Daily reminder off.");
    } catch {
      toast.error("Could not update. Try again.");
    }
  };

  return { progress, setProgress, videosAvailable, welcomeOpen, closeWelcome, tourOpen, setTourOpen, finishTour, applyCheckin, checkin, toggleDone, toggleReminder };
};
